import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createPaymentThunk } from "../../../../store/thunks/jewelleryPlan/paymentThunks";
import AmountInput from "../../../../components/JewelleryPlans/common/AmountInput";
import { PAYMENT_METHODS } from "../../../../constants/jewellery.constant";
import { CalendarIcon, UploadIcon } from "@heroicons/react/outline";
import { format } from "date-fns";

const PaymentForm = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedPlan, loading: planLoading } = useSelector(
    (state) => state.jewelleryPlan?.plans
  );
  const { loading: paymentLoading } = useSelector(
    (state) => state.jewelleryPlan?.payments
  );

  const [formData, setFormData] = useState({
    amountCollected: selectedPlan?.monthlyAmount || "",
    dateCollected: format(new Date(), "yyyy-MM-dd"),
    method: PAYMENT_METHODS.CASH,
    externalRef: "",
    note: "",
    proofFile: null,
  });

  const [errors, setErrors] = useState({});

  const loading = planLoading || paymentLoading;

  const handleAmountChange = (value) => {
    setFormData({ ...formData, amountCollected: value });
    if (errors.amountCollected) {
      setErrors({ ...errors, amountCollected: null });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, proofFile: "File size must be less than 5MB" });
        return;
      }
      setFormData({ ...formData, proofFile: file });
      setErrors({ ...errors, proofFile: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.amountCollected) {
      newErrors.amountCollected = "Amount is required";
    } else if (formData.amountCollected <= 0) {
      newErrors.amountCollected = "Amount must be greater than 0";
    }

    if (!formData.dateCollected) {
      newErrors.dateCollected = "Collection date is required";
    }

    if (!formData.method) {
      newErrors.method = "Payment method is required";
    }

    // External reference required for non-cash payments
    if (formData.method !== PAYMENT_METHODS.CASH && !formData.externalRef) {
      newErrors.externalRef = `${formData.method} reference is required`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      // Prepare payment data
      const paymentData = {
        planId,
        customerId: selectedPlan?.customerId || selectedPlan?.userId?._id,
        userId: selectedPlan?.userId?._id,
        branchId: selectedPlan?.branchId,
        amountCollected: parseFloat(formData.amountCollected),
        method: formData.method,
        dateCollected: new Date(formData.dateCollected).toISOString(),
        externalRef: formData.externalRef,
        note: formData.note,
      };

      // Handle file upload if present
      if (formData.proofFile) {
        console.log("File upload handling needed:", formData.proofFile);
      }

      // Create the payment
      await dispatch(createPaymentThunk(paymentData)).unwrap();

      // Navigate back to plan details with success message
      navigate(`/jewellery/${planId}`, {
        state: {
          message: "Payment recorded successfully. Awaiting verification.",
          type: "success",
        },
      });
    } catch (error) {
      console.error("Payment creation failed:", error);
      setErrors({
        submit: error || "Failed to record payment. Please try again.",
      });
    }
  };

  const getVariancePercent = () => {
    if (!selectedPlan?.monthlyAmount || !formData.amountCollected) return 0;
    return (
      ((formData.amountCollected - selectedPlan.monthlyAmount) /
        selectedPlan.monthlyAmount) *
      100
    );
  };

  const variancePercent = getVariancePercent();
  const hasHighVariance = Math.abs(variancePercent) > 10;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-background shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-text mb-6">Record Payment</h2>

        {/* Plan Info */}
        {selectedPlan && (
          <div className="bg-neutral-light rounded-lg p-4 mb-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-neutral-dark">Plan Number</p>
                <p className="font-medium">{selectedPlan.planNumber}</p>
              </div>
              <div>
                <p className="text-neutral-dark">Customer</p>
                <p className="font-medium">
                  {selectedPlan.userId?.name || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-neutral-dark">Monthly Amount</p>
                <p className="font-medium">
                  ₹{selectedPlan.monthlyAmount?.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-neutral-dark">Payments Done</p>
                <p className="font-medium">
                  {selectedPlan.paymentsDone || 0} /{" "}
                  {selectedPlan.templateSnapshot?.tenureMonths || 0}
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Amount Input */}
          <AmountInput
            label="Amount Collected"
            value={formData.amountCollected}
            onChange={handleAmountChange}
            error={errors.amountCollected}
            required
            showVariance={true}
            expectedAmount={selectedPlan?.monthlyAmount}
          />

          {/* Variance Warning */}
          {hasHighVariance && (
            <div className="bg-warning-light border border-warning rounded-lg p-3">
              <p className="text-sm text-warning-dark">
                ⚠️ High variance detected ({variancePercent.toFixed(1)}%). This
                payment will require additional approval.
              </p>
            </div>
          )}

          {/* Collection Date */}
          <div>
            <label className="block text-sm font-medium text-text">
              Collection Date <span className="text-danger">*</span>
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <CalendarIcon className="h-5 w-5 text-neutral-dark" />
              </div>
              <input
                type="date"
                value={formData.dateCollected}
                onChange={(e) =>
                  setFormData({ ...formData, dateCollected: e.target.value })
                }
                max={format(new Date(), "yyyy-MM-dd")}
                className={`block w-full pl-10 pr-3 py-2 border rounded-md focus:ring-primary focus:border-primary sm:text-sm
                  ${
                    errors.dateCollected
                      ? "border-danger-light"
                      : "border-neutral-light"
                  }`}
              />
            </div>
            {errors.dateCollected && (
              <p className="mt-1 text-sm text-danger">{errors.dateCollected}</p>
            )}
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-text">
              Payment Method <span className="text-danger">*</span>
            </label>
            <select
              value={formData.method}
              onChange={(e) =>
                setFormData({ ...formData, method: e.target.value })
              }
              className={`mt-1 block w-full border rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm
                ${
                  errors.method ? "border-danger-light" : "border-neutral-light"
                }`}
            >
              {Object.values(PAYMENT_METHODS).map((method) => (
                <option key={method} value={method}>
                  {method.replace("_", " ")}
                </option>
              ))}
            </select>
            {errors.method && (
              <p className="mt-1 text-sm text-danger">{errors.method}</p>
            )}
          </div>

          {/* External Reference */}
          {formData.method !== PAYMENT_METHODS.CASH && (
            <div>
              <label className="block text-sm font-medium text-text">
                {formData.method === PAYMENT_METHODS.UPI
                  ? "UPI Transaction ID"
                  : formData.method === PAYMENT_METHODS.CARD
                  ? "Card Transaction Reference"
                  : formData.method === PAYMENT_METHODS.BANK_TRANSFER
                  ? "Bank Reference Number"
                  : "Reference Number"}
                <span className="text-danger"> *</span>
              </label>
              <input
                type="text"
                value={formData.externalRef}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    externalRef: e.target.value,
                  })
                }
                className={`mt-1 block w-full border rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm
                  ${
                    errors.externalRef
                      ? "border-danger-light"
                      : "border-neutral-light"
                  }`}
                placeholder="Enter reference number"
              />
              {errors.externalRef && (
                <p className="mt-1 text-sm text-danger">{errors.externalRef}</p>
              )}
            </div>
          )}

          {/* Payment Proof Upload */}
          <div>
            <label className="block text-sm font-medium text-text">
              Payment Proof (Optional)
            </label>
            <div className="mt-1 flex items-center">
              <label className="relative cursor-pointer bg-background rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary">
                <span className="inline-flex items-center px-4 py-2 border border-neutral-light rounded-md shadow-sm text-sm font-medium text-text bg-background hover:bg-neutral-light">
                  <UploadIcon className="h-5 w-5 mr-2" />
                  {formData.proofFile ? formData.proofFile.name : "Choose file"}
                </span>
                <input
                  type="file"
                  className="sr-only"
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                />
              </label>
            </div>
            {errors.proofFile && (
              <p className="mt-1 text-sm text-danger">{errors.proofFile}</p>
            )}
            <p className="mt-1 text-xs text-neutral-dark">
              Upload receipt or screenshot (Max 5MB, JPG/PNG/PDF)
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-text">
              Notes (Optional)
            </label>
            <textarea
              value={formData.note}
              onChange={(e) =>
                setFormData({ ...formData, note: e.target.value })
              }
              rows={3}
              className="mt-1 block w-full border-neutral-light rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              placeholder="Any additional information..."
            />
          </div>

          {/* Error Message */}
          {errors.submit && (
            <div className="bg-danger-light border border-danger rounded-lg p-3">
              <p className="text-sm text-danger">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate(`/jewellery/${planId}`)}
              className="px-4 py-2 border border-neutral-light rounded-md text-sm font-medium text-text bg-background hover:bg-neutral-light"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-2 rounded-md text-sm font-medium text-text-on-primary
                ${
                  loading
                    ? "bg-neutral-dark cursor-not-allowed"
                    : "bg-primary hover:bg-primary-dark"
                }`}
            >
              {loading ? "Recording..." : "Record Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentForm;
